import {
  Announcement,
  CreateAnnouncementDto,
  UpdateAnnouncementDto,
  EventDetail,
  RecurringEventDetail,
  RecurringEventOccurrence,
  AnnouncementPrayerRequest
} from '../types/announcement';
import { baseBackendUrl } from '../service/baseService';

export const announcementService = {
  getAllAnnouncements: async (token: string): Promise<Announcement[]> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement`, {
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch announcements');
    return response.json();
  },

  getAnnouncementById: async (id: number, token: string): Promise<Announcement> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/${id}`, {
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch announcement');
    return response.json();
  },

  createAnnouncement: async (announcement: CreateAnnouncementDto, token: string): Promise<number> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(announcement)
    });
    if (!response.ok) throw new Error('Failed to create announcement');
    // Backend returns the created Announcement object via CreatedAtAction, not just the ID
    const data = await response.json();
    return typeof data === 'number' ? data : data?.announcementId;
  },

  updateAnnouncement: async (announcement: UpdateAnnouncementDto, token: string): Promise<void> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/${announcement.announcementId}`, {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify(announcement)
    });
    if (!response.ok) throw new Error('Failed to update announcement');
  },

  deleteAnnouncement: async (id: number, token: string): Promise<void> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error('Failed to delete announcement');
  },

  getEventDetail: async (announcementId: number, token: string): Promise<EventDetail> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/event/${announcementId}`, {
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch event detail');
    return response.json();
  },

  getRecurringEventOccurrences: async (announcementId: number, token: string): Promise<RecurringEventOccurrence[]> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/recurring/${announcementId}/occurrences`, {
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch recurring event occurrences');
    return response.json();
  },

  // Event Detail methods
  // Create a full Event Announcement using EventAnnouncementRequest payload
  createEventAnnouncement: async (
    eventRequest: {
      title: string;
      description: string;
      publishDate?: string;
      expiryDate?: string;
      startDateTime: string;
      endDateTime: string;
      location: string;
    },
    token: string
  ): Promise<number> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/event`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(eventRequest)
    });
    if (!response.ok) throw new Error('Failed to create event announcement');
    const data = await response.json();
    return typeof data === 'number' ? data : data?.announcementId;
  },

  createEventDetail: async (eventDetail: EventDetail, token: string): Promise<void> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/event`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(eventDetail)
    });
    if (!response.ok) throw new Error('Failed to create event detail');
  },

  updateEventDetail: async (eventDetail: EventDetail, token: string): Promise<void> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/event/${eventDetail.announcementId}`, {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify(eventDetail)
    });
    if (!response.ok) throw new Error('Failed to update event detail');
  },

  // Recurring Event methods
  // Create a full Recurring Event Announcement using RecurringEventAnnouncementRequest payload
  createRecurringEventAnnouncement: async (
    recurringRequest: {
      title: string;
      description: string;
      publishDate?: string;
      expiryDate?: string;
      location: string;
      occurrences: Array<{
        startDateTime: string;
        endDateTime: string;
        metaData?: string;
      }>
    },
    token: string
  ): Promise<number> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/recurring-event`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(recurringRequest)
    });
    if (!response.ok) throw new Error('Failed to create recurring event announcement');
    const data = await response.json();
    return typeof data === 'number' ? data : data?.announcementId;
  },

  createRecurringEventDetail: async (recurringEventDetail: RecurringEventDetail, token: string): Promise<void> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/recurring`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(recurringEventDetail)
    });
    if (!response.ok) throw new Error('Failed to create recurring event detail');
  },

  updateRecurringEventDetail: async (recurringEventDetail: RecurringEventDetail, token: string): Promise<void> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/recurring/${recurringEventDetail.announcementId}`, {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify(recurringEventDetail)
    });
    if (!response.ok) throw new Error('Failed to update recurring event detail');
  },

  // Prayer Request methods
  createPrayerRequest: async (prayerRequest: AnnouncementPrayerRequest, token: string): Promise<void> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/prayer`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(prayerRequest)
    });
    if (!response.ok) throw new Error('Failed to create prayer request');
  },

  updatePrayerRequest: async (prayerRequest: AnnouncementPrayerRequest, token: string): Promise<void> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/prayer/${prayerRequest.announcementId}`, {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify(prayerRequest)
    });
    if (!response.ok) throw new Error('Failed to update prayer request');
  },

  getPrayerRequest: async (announcementId: number, token: string): Promise<AnnouncementPrayerRequest> => {
    const headers: Record<string, string> = token ? { 'Authorization': `Bearer ${token}` } : {};
    const response = await fetch(`${baseBackendUrl}/api/Announcement/prayer/${announcementId}`, {
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch prayer request');
    return response.json();
  },

  getUniqueLocations: async (token: string): Promise<string[]> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseBackendUrl}/api/Announcement/locations`, {
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch locations');
    return response.json();
  }
};